import { Box } from "@outschool/backpack";
import {
  CurrentLearnerNeedsConsentQuery,
  CurrentLearnerNeedsConsentQueryVariables,
  SetCurrentLearnerConsentMutation,
  SetCurrentLearnerConsentMutationVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useMutation, useQuery } from "@outschool/ui-apollo";
import {
  LearnerHealthConsentModal,
  LearnerMarketingConsentModal,
} from "@outschool/ui-components-shared";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useCurrentLearner } from "../providers/CurrentLearnerProvider";

const OS_MODAL_SLIDE_TRANSITION_MS = 300;

const currentLearnerNeedsConsentQuery = gql`
  query CurrentLearnerNeedsConsent {
    currentLearnerNeedsConsent {
      needsMarketingConsent
      needsHealthConsent
    }
  }
`;

const setCurrentLearnerConsentMutation = gql`
  mutation SetCurrentLearnerConsent(
    $hasMarketingConsent: Boolean
    $hasHealthConsent: Boolean
  ) {
    setCurrentLearnerConsent(
      hasMarketingConsent: $hasMarketingConsent
      hasHealthConsent: $hasHealthConsent
    ) {
      uid
    }
  }
`;

interface PostLoginLearnerConsentProps {
  onDone: () => void;
}

export function PostLoginLearnerConsent({
  onDone,
}: PostLoginLearnerConsentProps) {
  const currentLearner = useCurrentLearner();
  const learnerName = currentLearner?.name ?? "";

  const { data, loading } = useQuery<
    CurrentLearnerNeedsConsentQuery,
    CurrentLearnerNeedsConsentQueryVariables
  >(currentLearnerNeedsConsentQuery);
  const [setConsent] = useMutation<
    SetCurrentLearnerConsentMutation,
    SetCurrentLearnerConsentMutationVariables
  >(setCurrentLearnerConsentMutation);

  const needsMarketingConsent =
    data?.currentLearnerNeedsConsent?.needsMarketingConsent ?? false;
  const needsHealthConsent =
    data?.currentLearnerNeedsConsent?.needsHealthConsent ?? false;

  const [isConsentFlowActive, setIsConsentFlowActive] = useState(false);
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);
  const [isHealthModalOpen, setIsHealthModalOpen] = useState(false);

  const marketingConsentRef = useRef<boolean | null>(null);
  const healthConsentRef = useRef<boolean | null>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (loading || !data || hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    if (needsMarketingConsent || needsHealthConsent) {
      setIsConsentFlowActive(true);
      if (needsMarketingConsent) {
        setIsMarketingModalOpen(true);
      }
      if (needsHealthConsent) {
        setIsHealthModalOpen(true);
      }
    } else {
      onDone();
    }
  }, [loading, data, needsMarketingConsent, needsHealthConsent, onDone]);

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  const finishConsentFlow = useCallback(async () => {
    await setConsent({
      variables: {
        hasMarketingConsent: marketingConsentRef.current,
        hasHealthConsent: healthConsentRef.current,
      },
    });
    setIsConsentFlowActive(false);
    onDone();
  }, [setConsent, onDone]);

  const handleMarketingModalClose = useCallback(() => {
    setIsMarketingModalOpen(false);

    if (needsHealthConsent) {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      transitionTimeoutRef.current = setTimeout(() => {
        setIsHealthModalOpen(true);
      }, OS_MODAL_SLIDE_TRANSITION_MS);
    } else {
      finishConsentFlow();
    }
  }, [needsHealthConsent, finishConsentFlow]);

  const handleHealthModalClose = useCallback(() => {
    setIsHealthModalOpen(false);
    finishConsentFlow();
  }, [finishConsentFlow]);

  if (loading || !data) {
    return null;
  }

  if (!needsMarketingConsent && !needsHealthConsent) {
    return null;
  }

  return (
    <>
      {isConsentFlowActive && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1200,
          }}
        />
      )}

      <LearnerMarketingConsentModal
        open={isMarketingModalOpen}
        learnerName={learnerName}
        onSave={hasAgreed => {
          marketingConsentRef.current = hasAgreed;
        }}
        onClose={handleMarketingModalClose}
      />

      <LearnerHealthConsentModal
        open={isHealthModalOpen}
        learnerName={learnerName}
        onSave={hasAgreed => {
          healthConsentRef.current = hasAgreed;
        }}
        onClose={handleHealthModalClose}
      />
    </>
  );
}
